import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { collection, addDoc, onSnapshot, orderBy, query, CollectionReference, DocumentData, Timestamp, updateDoc, getDocs, doc } from 'firebase/firestore';

import { AuthService } from './auth.service';
import { FirebaseService } from './firebase.service';

export interface Notification {
  id?: string;
  createdAt: Date | Timestamp;
  emailedOn: Date | Timestamp | null;
  watcherId: string;
  type: string;
  seen: boolean;
  notificationFlightOptions: string[];
  message: string;
  matchType: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private notificationsSubject = new BehaviorSubject<Notification[]>([]);
  public readonly notifications$: Observable<Notification[]> = this.notificationsSubject.asObservable();

  private notificationsUnsubscribe?: () => void;

  constructor(private firebaseService: FirebaseService, private authService: AuthService) {
    this.authService.user$.subscribe(user => {
      if (user) {
        this.listenToNotifications(user.uid);
      } else {
        this.stopListeningToNotifications();
        this.notificationsSubject.next([]);
      }
    });
  }

  private listenToNotifications(uid: string) {
    const collectionRef: CollectionReference<DocumentData> = collection(
      this.firebaseService.firestore,
      'users',
      uid,
      'notifications'
    );
    const q = query(collectionRef, orderBy('createdAt', 'desc'));

    // save unsubscribe ref
    this.notificationsUnsubscribe = onSnapshot(q, snapshot => {
      const notes = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Notification[];
      this.notificationsSubject.next(notes);
    });
  }

  public async stopListeningToNotifications(): Promise<void> {
    if (this.notificationsUnsubscribe) {
      this.notificationsUnsubscribe();
      this.notificationsUnsubscribe = undefined;
    }
    this.notificationsSubject.next([]);
  }

  async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>) {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found.');
    }
    const collectionRef = collection(this.firebaseService.firestore, 'users', user.uid, 'notifications');
    await addDoc(collectionRef, { ...notification, createdAt: new Date(), seen: false });
  }

  async markAllAsSeen(): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user found.');
    }

    const collectionRef = collection(this.firebaseService.firestore, 'users', user.uid, 'notifications');
    const snapshot = await getDocs(collectionRef);

    const updates = snapshot.docs.map(d =>
      updateDoc(doc(this.firebaseService.firestore, 'users', user.uid, 'notifications', d.id), {
        seen: true
      })
    );

    await Promise.all(updates);
  }
}

